import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGetZoho, adminSaveZoho, adminTestZoho, adminZohoLists, adminZohoSyncContact } from "@/lib/api";
import { Megaphone, Loader2, Save, ShieldCheck, Plug, CheckCircle2, RefreshCw, UserPlus } from "lucide-react";

const inputCls = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";
const REGIONS = [["com", "US (.com)"], ["in", "India (.in)"], ["eu", "Europe (.eu)"], ["com.au", "Australia (.com.au)"], ["jp", "Japan (.jp)"]];

export default function AdminZohoCampaigns() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ region: "com", client_id: "", client_secret: "", refresh_token: "", default_listkey: "", from_email: "", from_name: "", enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lists, setLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [syncEmail, setSyncEmail] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = () => adminGetZoho().then((d) => {
    setCfg(d);
    setForm((f) => ({ ...f, region: d.region || "com", default_listkey: d.default_listkey || "", from_email: d.from_email || "", from_name: d.from_name || "", enabled: !!d.enabled }));
  });
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="py-20 text-center text-ink-3"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { region: form.region, default_listkey: form.default_listkey, from_email: form.from_email, from_name: form.from_name, enabled: form.enabled };
      ["client_id", "client_secret", "refresh_token"].forEach((k) => { if (form[k]) payload[k] = form[k]; });
      const d = await adminSaveZoho(payload);
      setCfg(d); setForm((f) => ({ ...f, client_id: "", client_secret: "", refresh_token: "" }));
      toast.success("Zoho Campaigns settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true);
    try { const d = await adminTestZoho(); toast.success(d.message || "Connected."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Connection failed."); }
    finally { setTesting(false); }
  };
  const fetchLists = async () => {
    setLoadingLists(true);
    try { const d = await adminZohoLists(); setLists(d.items || []); if (!d.items?.length) toast.info("No mailing lists found in Zoho."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not fetch lists."); }
    finally { setLoadingLists(false); }
  };
  const syncContact = async () => {
    if (!syncEmail.trim()) return;
    setSyncing(true);
    try { const d = await adminZohoSyncContact({ email: syncEmail.trim() }); toast.success(d.message || "Synced."); setSyncEmail(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed."); }
    finally { setSyncing(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-zoho-campaigns">
      <div className="mb-5 flex items-center gap-3">
        <Megaphone className="h-6 w-6 text-saffron" />
        <div>
          <h1 className="font-serif-latin text-2xl text-ink">Zoho Campaigns</h1>
          <p className="text-sm text-ink-3">Route marketing &amp; broadcast emails (announcements, digests, newsletters, app-launch) through Zoho Campaigns. Add your API credentials below.</p>
        </div>
      </div>

      <div className="mb-5 rounded border-l-2 border-gold bg-[rgba(176,141,87,0.09)] px-4 py-3 text-sm text-ink-2">
        <strong className="text-ink">Note:</strong> Zoho does not permit transactional email (password reset, verification, receipts) via Campaigns — those keep using your Zoho SMTP. When <em>enabled &amp; connected</em>, your broadcast campaigns (Email &amp; Campaigns → Compose) are sent through Zoho Campaigns automatically.
      </div>

      <div className="mb-5 flex items-center gap-2 text-sm">
        {cfg.configured
          ? <span className="inline-flex items-center gap-1.5 text-green-700" data-testid="zc-connected"><CheckCircle2 className="h-4 w-4" /> Connected · {cfg.region} {cfg.enabled ? "· routing ON" : "· routing OFF"}</span>
          : <span className="text-ink-2" data-testid="zc-disconnected">Not connected — enter your Zoho self-client credentials.</span>}
      </div>

      <div className="space-y-4 border border-line bg-surface p-6">
        <label className="flex items-center gap-3 text-sm text-ink-2">
          <input type="checkbox" data-testid="zc-enabled" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4" />
          Route broadcast campaigns through Zoho Campaigns
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Data center</label>
            <select data-testid="zc-region" value={form.region} onChange={(e) => set("region", e.target.value)} className={inputCls}>
              {REGIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Default List Key</label>
            <div className="flex gap-1.5">
              <input data-testid="zc-listkey" value={form.default_listkey} onChange={(e) => set("default_listkey", e.target.value)} className={inputCls} placeholder="mailing list key" />
              <button onClick={fetchLists} disabled={loadingLists} data-testid="zc-fetch-lists" title="Fetch my lists" className="inline-flex items-center gap-1 border border-line px-2.5 text-xs text-ink-2 hover:border-saffron hover:text-saffron">
                {loadingLists ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
            </div>
            {lists.length > 0 && (
              <select data-testid="zc-list-select" onChange={(e) => set("default_listkey", e.target.value)} className={`${inputCls} mt-1.5`} defaultValue="">
                <option value="" disabled>Pick a list…</option>
                {lists.map((l) => <option key={l.listkey} value={l.listkey}>{l.listname}</option>)}
              </select>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Client ID {cfg.client_id_set && <span className="normal-case text-ink-3">(stored — leave blank to keep)</span>}</label>
            <input data-testid="zc-client-id" value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className={inputCls} placeholder="1000.XXXXXXXX" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Client Secret</label>
            <input data-testid="zc-client-secret" type="password" value={form.client_secret} onChange={(e) => set("client_secret", e.target.value)} className={inputCls} placeholder={cfg.secret_set ? "•••••••• stored" : "client secret"} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">Refresh Token</label>
            <input data-testid="zc-refresh" type="password" value={form.refresh_token} onChange={(e) => set("refresh_token", e.target.value)} className={inputCls} placeholder={cfg.refresh_set ? "•••••••• stored" : "1000.XXXX..."} />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">From email</label>
            <input data-testid="zc-from-email" value={form.from_email} onChange={(e) => set("from_email", e.target.value)} className={inputCls} placeholder="news@dharmartha.org" />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-ink-3">From name</label>
            <input data-testid="zc-from-name" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} className={inputCls} placeholder="Dharmartha" />
          </div>
        </div>
        {cfg.configured && <p className="flex items-center gap-2 text-xs text-green-700"><ShieldCheck className="h-4 w-4" /> Credentials stored securely. Blank secret fields keep the saved value.</p>}
        <p className="text-xs text-ink-3">Get these from <span className="text-ink-2">Zoho API Console → Self Client</span> with scopes <code>ZohoCampaigns.contact.ALL, ZohoCampaigns.campaign.ALL</code>, then exchange the code for a refresh token.</p>
        <div className="flex flex-wrap gap-3 border-t border-line pt-4">
          <button onClick={save} disabled={saving} data-testid="zc-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
          </button>
          <button onClick={test} disabled={testing} data-testid="zc-test" className="inline-flex items-center gap-2 border border-saffron px-5 py-2.5 text-sm text-saffron transition-colors hover:bg-saffron hover:text-white disabled:opacity-60">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test connection
          </button>
        </div>
      </div>

      <div className="mt-6 border border-line bg-surface p-6">
        <h2 className="mb-1 font-serif-latin text-lg text-ink">Add a contact to your list</h2>
        <p className="mb-4 text-sm text-ink-3">New waitlist &amp; app-launch sign-ups sync automatically. Add one manually to test.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1"><input data-testid="zc-sync-email" value={syncEmail} onChange={(e) => setSyncEmail(e.target.value)} className={inputCls} placeholder="contact@example.com" /></div>
          <button onClick={syncContact} disabled={syncing} data-testid="zc-sync" className="inline-flex items-center gap-2 border border-line px-5 py-2.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Sync contact
          </button>
        </div>
      </div>
    </div>
  );
}
